import { createReducer, on } from '@ngrx/store';
import { last, set } from 'lodash';
import { IGeneralVariablesDataOptionV3 } from './general-new.interface';
import { QuestionnaireGeneralActions } from './general.actions';
import { GENERAL_VARIABLE_DATA } from './general.data';
import { IGeneralVariablesDataOption } from './general.interface';
import {
  QuestionnaireGeneralState,
  intialQuestionnaireGeneralState,
  questionnaireGeneralState
} from './general.state';

/**
 * Type guard that ensures the provided value is an array of V3 option objects.
 *
 * @param {unknown} value Value to verify.
 * @returns {boolean} True when the value is an option array.
 */
const isOptionV3Array = (
  value: unknown
): value is Array<IGeneralVariablesDataOptionV3> =>
  Array.isArray(value) &&
  value.every(
    (item): item is IGeneralVariablesDataOptionV3 =>
      typeof item === 'object' && item !== null && 'value' in item
  );

export const questionnaireGeneralReducer = createReducer(
  questionnaireGeneralState,
  on(
    QuestionnaireGeneralActions.reset,
    (): QuestionnaireGeneralState => intialQuestionnaireGeneralState()
  ),
  on(
    QuestionnaireGeneralActions.setActive,
    (state, { active }): QuestionnaireGeneralState => ({
      ...state,
      active
    })
  ),
  on(
    QuestionnaireGeneralActions.setForm,
    (state, { key, value }): QuestionnaireGeneralState => ({
      ...state,
      form: {
        ...state.form,
        ...set(state.form, key, value)
      }
    })
  ),
  on(
    QuestionnaireGeneralActions.setFormV3,
    (state, { key, value }): QuestionnaireGeneralState => {
      if (isOptionV3Array(value)) {
        const baseValue = value.filter((val) => val.value !== 'OTHER');
        const valueOther = value.filter((val) => val.value === 'OTHER');
        const lastValueOther = last(valueOther);
        const valueOption =
          valueOther.length > 0 && lastValueOther
            ? [...baseValue, lastValueOther]
            : [...baseValue];

        return {
          ...state,
          formV3: {
            ...state.formV3,
            [key]: valueOption
          }
        };
      }

      return {
        ...state,
        formV3: {
          ...state.formV3,
          [key]: value
        }
      };
    }
  ),
  on(
    QuestionnaireGeneralActions.setLoading,
    (state, { key, value }): QuestionnaireGeneralState => ({
      ...state,
      loading: {
        ...state.loading,
        [key]: value
      }
    })
  ),
  on(
    QuestionnaireGeneralActions.setModal,
    (state, { key, value }): QuestionnaireGeneralState => ({
      ...state,
      modal: {
        ...state.modal,
        [key]: value
      }
    })
  ),
  on(
    QuestionnaireGeneralActions.setMasterDisease,
    (state, { count, results = [] }): QuestionnaireGeneralState => {
      // PURPOSE: Update the GENERAL_VARIABLE_DATA['healthMedicalHistories']
      // with the fetched results
      if (GENERAL_VARIABLE_DATA.healthMedicalHistories && results.length > 0) {
        const items = results.map((item) => ({
          ...item,
          value: item.name,
          isDefault: false
        })) as Array<IGeneralVariablesDataOption>;
        GENERAL_VARIABLE_DATA.healthMedicalHistories.options = [
          {
            name: 'None',
            value: 'None',
            description: '',
            isDefault: true
          },
          ...items
        ];
      }
      return {
        ...state,
        masterDisease: { count, results }
      };
    }
  ),
  on(
    QuestionnaireGeneralActions.setMasterMedication,
    (state, { count, results }): QuestionnaireGeneralState => ({
      ...state,
      masterMedication: { count, results }
    })
  ),
  on(
    QuestionnaireGeneralActions.setSubmissionResponse,
    (
      state,
      { success, response, message = null }
    ): QuestionnaireGeneralState => ({
      ...state,
      submissionResponse: {
        success,
        message,
        response
      }
    })
  )
);
